import type { Car, FuelActivityFilters, FuelLog, GasStation, GasStationBrand, User } from "@/types/database";
import { carTitle, userDisplayName } from "@/lib/utils";

export type FuelDateRange = FuelActivityFilters["dateRange"];

export type FuelLogEnriched = FuelLog & {
  user: User | null;
  car: Car | null;
  station: GasStation | null;
};

export type FuelUserSummary = {
  userID: string;
  user: User | null;
  fillUps: number;
  totalSpent: number;
  totalLitres: number;
  avgPricePerLitre: number;
  avgFillCost: number;
  lastLoggedAt: string | null;
  topGrade: string | null;
  topStationId: string | null;
  carIds: string[];
};

export type FuelPlatformOverview = {
  totalFillUps: number;
  totalSpent: number;
  totalLitres: number;
  activeUsers: number;
  avgPricePerLitre: number;
  avgFillCost: number;
};

export type FuelChartDatum = { name: string; value: number; fill?: string };

export type FuelActivityCharts = {
  spendByWeek: FuelChartDatum[];
  spendByGrade: FuelChartDatum[];
  topUsersBySpend: FuelChartDatum[];
  fillUpsByMonth: FuelChartDatum[];
  fillUpsByDayOfWeek: FuelChartDatum[];
};

export type FuelPopularityMetrics = {
  topDayOfWeek: { day: string; fillUps: number; sharePercent: number } | null;
  topBrand: { brandName: string; fillUps: number; sharePercent: number } | null;
  topStation: { name: string; address: string; fillUps: number; sharePercent: number } | null;
};

export type UserFuelCharts = {
  spendTimeline: FuelChartDatum[];
  spendByCar: FuelChartDatum[];
  spendByGrade: FuelChartDatum[];
  spendByStation: FuelChartDatum[];
  litresTimeline: FuelChartDatum[];
  pricePerLitreTimeline: FuelChartDatum[];
};

export type FuelInsight = {
  id: string;
  tone: "neutral" | "positive" | "warning";
  title: string;
  detail: string;
  href?: string;
};

export type FuelYearSummary = {
  year: number;
  overview: FuelPlatformOverview;
  isCurrentYear: boolean;
};

export type YearlySpendMonthRow = {
  month: string;
  monthIndex: number;
  spent: number;
  fillUps: number;
  litres: number;
};

export type YearlySpendBreakdown = {
  year: number;
  isCurrentYear: boolean;
  overview: FuelPlatformOverview;
  vsPriorYearPercent: number | null;
  priorYearSpent: number | null;
  spendByMonth: FuelChartDatum[];
  spendByGrade: FuelChartDatum[];
  spendByUser: FuelChartDatum[];
  spendByCar: FuelChartDatum[];
  spendByStation: FuelChartDatum[];
  monthRows: YearlySpendMonthRow[];
};

const GRADE_COLORS: Record<string, string> = {
  "87": "#34c759",
  "90": "#ffcc00",
  diesel: "#8e8e93",
  ulsd: "#af52de",
  "E-10 87": "#34c759",
  "E-10 90": "#ffcc00",
  Diesel: "#8e8e93",
  ULSD: "#af52de",
};

export function fuelGradeColor(grade: string): string {
  return GRADE_COLORS[grade] ?? "#007aff";
}

export function defaultFuelActivityFilters(): FuelActivityFilters {
  return {
    search: "",
    dateRange: "30d",
    fuelGrade: "",
    carID: "",
    gasStationID: "",
    sort: "spent",
  };
}

export function dateRangeStart(range: FuelDateRange): Date | null {
  const now = new Date();
  if (range === "all") return null;
  if (range === "ytd") return new Date(now.getFullYear(), 0, 1);
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
  const start = new Date(now);
  start.setDate(start.getDate() - days);
  start.setHours(0, 0, 0, 0);
  return start;
}

export function filterLogsByDateRange(logs: FuelLog[], range: FuelDateRange): FuelLog[] {
  const start = dateRangeStart(range);
  if (!start) return logs;
  const startMs = start.getTime();
  return logs.filter((log) => new Date(log.loggedAt).getTime() >= startMs);
}

export function enrichFuelLogs(
  logs: FuelLog[],
  users: User[],
  cars: Car[],
  stations: GasStation[]
): FuelLogEnriched[] {
  const userById = new Map(users.filter((u) => u.userID).map((u) => [u.userID as string, u]));
  const carById = new Map(cars.filter((c) => c.carID).map((c) => [c.carID, c]));
  const stationByUuid = new Map(
    stations.filter((s) => s.stationID).map((s) => [s.stationID as string, s])
  );

  return logs.map((log) => ({
    ...log,
    user: userById.get(log.userID) ?? null,
    car: carById.get(log.carID) ?? null,
    station: stationByUuid.get(log.gasStationID) ?? null,
  }));
}

function weightedAvgPrice(logs: FuelLog[]): number {
  const litres = logs.reduce((sum, log) => sum + log.litres, 0);
  if (litres <= 0) return 0;
  return logs.reduce((sum, log) => sum + log.totalCost, 0) / litres;
}

function modeKey<T extends string>(counts: Map<T, number>): T | null {
  let best: T | null = null;
  let bestCount = 0;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      best = key;
      bestCount = count;
    }
  }
  return best;
}

export function buildFuelUserSummaries(
  logs: FuelLog[],
  users: User[]
): FuelUserSummary[] {
  const userById = new Map(users.filter((u) => u.userID).map((u) => [u.userID as string, u]));
  const byUser = new Map<string, FuelLog[]>();

  for (const log of logs) {
    const list = byUser.get(log.userID) ?? [];
    list.push(log);
    byUser.set(log.userID, list);
  }

  const summaries: FuelUserSummary[] = [];

  for (const [userID, userLogs] of byUser) {
    const gradeCounts = new Map<string, number>();
    const stationCounts = new Map<string, number>();
    const carIds = new Set<string>();
    let lastLoggedAt: string | null = null;

    for (const log of userLogs) {
      carIds.add(log.carID);
      gradeCounts.set(log.fuelGrade, (gradeCounts.get(log.fuelGrade) ?? 0) + 1);
      stationCounts.set(log.gasStationID, (stationCounts.get(log.gasStationID) ?? 0) + 1);
      if (!lastLoggedAt || new Date(log.loggedAt) > new Date(lastLoggedAt)) {
        lastLoggedAt = log.loggedAt;
      }
    }

    const totalSpent = userLogs.reduce((sum, log) => sum + log.totalCost, 0);
    const totalLitres = userLogs.reduce((sum, log) => sum + log.litres, 0);

    summaries.push({
      userID,
      user: userById.get(userID) ?? null,
      fillUps: userLogs.length,
      totalSpent,
      totalLitres,
      avgPricePerLitre: weightedAvgPrice(userLogs),
      avgFillCost: userLogs.length ? totalSpent / userLogs.length : 0,
      lastLoggedAt,
      topGrade: modeKey(gradeCounts),
      topStationId: modeKey(stationCounts),
      carIds: [...carIds],
    });
  }

  return summaries;
}

export function sortFuelUserSummaries(
  summaries: FuelUserSummary[],
  sort: FuelActivityFilters["sort"]
): FuelUserSummary[] {
  const copy = [...summaries];
  copy.sort((a, b) => {
    switch (sort) {
      case "fillUps":
        return b.fillUps - a.fillUps;
      case "litres":
        return b.totalLitres - a.totalLitres;
      case "recent":
        return (b.lastLoggedAt ? new Date(b.lastLoggedAt).getTime() : 0) -
          (a.lastLoggedAt ? new Date(a.lastLoggedAt).getTime() : 0);
      case "spent":
      default:
        return b.totalSpent - a.totalSpent;
    }
  });
  return copy;
}

export function getFuelPlatformOverview(logs: FuelLog[]): FuelPlatformOverview {
  const userIds = new Set(logs.map((log) => log.userID));
  const totalSpent = logs.reduce((sum, log) => sum + log.totalCost, 0);
  const totalLitres = logs.reduce((sum, log) => sum + log.litres, 0);

  return {
    totalFillUps: logs.length,
    totalSpent,
    totalLitres,
    activeUsers: userIds.size,
    avgPricePerLitre: weightedAvgPrice(logs),
    avgFillCost: logs.length ? totalSpent / logs.length : 0,
  };
}

function weekLabel(date: Date): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

function monthLabel(date: Date): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", year: "2-digit" }).format(date);
}

const WEEKDAYS_MON_FIRST = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

function dayOfWeekName(date: Date): string {
  return new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date);
}

function topCountEntry<T extends string>(
  counts: Map<T, number>,
  total: number
): { key: T; count: number; sharePercent: number } | null {
  let bestKey: T | null = null;
  let bestCount = 0;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      bestKey = key;
      bestCount = count;
    }
  }
  if (!bestKey || bestCount === 0) return null;
  return {
    key: bestKey,
    count: bestCount,
    sharePercent: total ? Math.round((bestCount / total) * 100) : 0,
  };
}

export function getFuelPopularityMetrics(
  logs: FuelLog[],
  stations: GasStation[],
  brands: GasStationBrand[]
): FuelPopularityMetrics {
  if (logs.length === 0) {
    return { topDayOfWeek: null, topBrand: null, topStation: null };
  }

  const stationByUuid = new Map(
    stations.filter((s) => s.stationID).map((s) => [s.stationID as string, s])
  );
  const brandById = new Map(brands.map((brand) => [brand.id, brand]));

  const dayCounts = new Map<string, number>();
  const brandCounts = new Map<string, number>();
  const stationCounts = new Map<string, number>();

  for (const log of logs) {
    const day = dayOfWeekName(new Date(log.loggedAt));
    dayCounts.set(day, (dayCounts.get(day) ?? 0) + 1);

    const station = stationByUuid.get(log.gasStationID);
    const brandName = station?.brandID
      ? brandById.get(station.brandID)?.brandName ?? station.name
      : station?.name ?? "Unknown brand";
    brandCounts.set(brandName, (brandCounts.get(brandName) ?? 0) + 1);

    stationCounts.set(log.gasStationID, (stationCounts.get(log.gasStationID) ?? 0) + 1);
  }

  const topDay = topCountEntry(dayCounts, logs.length);
  const topBrandEntry = topCountEntry(brandCounts, logs.length);
  const topStationEntry = topCountEntry(stationCounts, logs.length);

  const topStationRecord = topStationEntry
    ? stationByUuid.get(topStationEntry.key)
    : null;

  return {
    topDayOfWeek: topDay
      ? { day: topDay.key, fillUps: topDay.count, sharePercent: topDay.sharePercent }
      : null,
    topBrand: topBrandEntry
      ? {
          brandName: topBrandEntry.key,
          fillUps: topBrandEntry.count,
          sharePercent: topBrandEntry.sharePercent,
        }
      : null,
    topStation: topStationEntry
      ? {
          name: topStationRecord?.name ?? topStationEntry.key.slice(0, 8),
          address: topStationRecord?.address ?? "Address unavailable",
          fillUps: topStationEntry.count,
          sharePercent: topStationEntry.sharePercent,
        }
      : null,
  };
}

function fillUpsByDayOfWeekChart(logs: FuelLog[]): FuelChartDatum[] {
  const dayCounts = new Map<string, number>();
  for (const log of logs) {
    const day = dayOfWeekName(new Date(log.loggedAt));
    dayCounts.set(day, (dayCounts.get(day) ?? 0) + 1);
  }

  let peakDay: string | null = null;
  let peakCount = 0;
  for (const [day, count] of dayCounts) {
    if (count > peakCount) {
      peakDay = day;
      peakCount = count;
    }
  }

  return WEEKDAYS_MON_FIRST.map((day) => ({
    name: day.slice(0, 3),
    value: dayCounts.get(day) ?? 0,
    fill: day === peakDay ? "#007aff" : "#c7c7cc",
  }));
}

export function getFuelActivityCharts(logs: FuelLog[], users: User[]): FuelActivityCharts {
  const weekSpend = new Map<string, number>();
  const gradeSpend = new Map<string, number>();
  const monthFillUps = new Map<string, number>();

  for (const log of logs) {
    const logged = new Date(log.loggedAt);
    const weekKey = weekLabel(logged);
    weekSpend.set(weekKey, (weekSpend.get(weekKey) ?? 0) + log.totalCost);

    const grade = log.fuelGrade;
    gradeSpend.set(grade, (gradeSpend.get(grade) ?? 0) + log.totalCost);

    const monthKey = monthLabel(logged);
    monthFillUps.set(monthKey, (monthFillUps.get(monthKey) ?? 0) + 1);
  }

  const userSummaries = sortFuelUserSummaries(buildFuelUserSummaries(logs, users), "spent").slice(0, 8);

  return {
    spendByWeek: [...weekSpend.entries()]
      .slice(-12)
      .map(([name, value]) => ({ name, value: Math.round(value) })),
    spendByGrade: [...gradeSpend.entries()].map(([name, value]) => ({
      name,
      value: Math.round(value),
      fill: fuelGradeColor(name),
    })),
    topUsersBySpend: userSummaries.map((summary) => ({
      name: summary.user ? userDisplayName(summary.user) : summary.userID.slice(0, 8),
      value: Math.round(summary.totalSpent),
    })),
    fillUpsByMonth: [...monthFillUps.entries()].slice(-12).map(([name, value]) => ({ name, value })),
    fillUpsByDayOfWeek: fillUpsByDayOfWeekChart(logs),
  };
}

export function getUserFuelCharts(logs: FuelLog[], cars: Car[], stations: GasStation[]): UserFuelCharts {
  const carById = new Map(cars.map((c) => [c.carID, c]));
  const stationByUuid = new Map(stations.filter((s) => s.stationID).map((s) => [s.stationID as string, s]));

  const timeline = new Map<string, { spent: number; litres: number; priceSum: number; count: number }>();
  const carSpend = new Map<string, number>();
  const gradeSpend = new Map<string, number>();
  const stationSpend = new Map<string, number>();

  for (const log of logs) {
    const key = weekLabel(new Date(log.loggedAt));
    const bucket = timeline.get(key) ?? { spent: 0, litres: 0, priceSum: 0, count: 0 };
    bucket.spent += log.totalCost;
    bucket.litres += log.litres;
    bucket.priceSum += log.pricePerLitre;
    bucket.count += 1;
    timeline.set(key, bucket);

    carSpend.set(log.carID, (carSpend.get(log.carID) ?? 0) + log.totalCost);
    gradeSpend.set(log.fuelGrade, (gradeSpend.get(log.fuelGrade) ?? 0) + log.totalCost);
    stationSpend.set(log.gasStationID, (stationSpend.get(log.gasStationID) ?? 0) + log.totalCost);
  }

  const timelineEntries = [...timeline.entries()].slice(-12);

  return {
    spendTimeline: timelineEntries.map(([name, bucket]) => ({ name, value: Math.round(bucket.spent) })),
    litresTimeline: timelineEntries.map(([name, bucket]) => ({
      name,
      value: Math.round(bucket.litres * 10) / 10,
    })),
    pricePerLitreTimeline: timelineEntries.map(([name, bucket]) => ({
      name,
      value: bucket.count ? Math.round((bucket.priceSum / bucket.count) * 100) / 100 : 0,
    })),
    spendByCar: [...carSpend.entries()].map(([carID, value]) => ({
      name: carById.get(carID) ? carTitle(carById.get(carID)!) : carID.slice(0, 8),
      value: Math.round(value),
    })),
    spendByGrade: [...gradeSpend.entries()].map(([name, value]) => ({
      name,
      value: Math.round(value),
      fill: fuelGradeColor(name),
    })),
    spendByStation: [...stationSpend.entries()].map(([stationId, value]) => {
      const station = stationByUuid.get(stationId);
      return {
        name: station?.name ?? stationId.slice(0, 8),
        value: Math.round(value),
      };
    }),
  };
}

export function buildFuelInsights(
  logs: FuelLog[],
  previousLogs: FuelLog[],
  userLabel?: string
): FuelInsight[] {
  const insights: FuelInsight[] = [];
  const prefix = userLabel ? `${userLabel}: ` : "";

  const spent = logs.reduce((sum, log) => sum + log.totalCost, 0);
  const prevSpent = previousLogs.reduce((sum, log) => sum + log.totalCost, 0);

  if (logs.length === 0) {
    insights.push({
      id: "empty",
      tone: "neutral",
      title: `${prefix}No fuel logs in this period`,
      detail: "Try widening the date range or check that users are logging top-ups in the app.",
    });
    return insights;
  }

  if (prevSpent > 0) {
    const delta = ((spent - prevSpent) / prevSpent) * 100;
    insights.push({
      id: "spend-delta",
      tone: delta > 10 ? "warning" : delta < -5 ? "positive" : "neutral",
      title: `${prefix}${delta >= 0 ? "Up" : "Down"} ${Math.abs(Math.round(delta))}% vs prior period`,
      detail: `Filtered period spend ${spent.toLocaleString()} JMD compared with ${prevSpent.toLocaleString()} JMD in the previous window.`,
    });
  }

  const avgPrice = weightedAvgPrice(logs);
  const expensive = [...logs].sort((a, b) => b.pricePerLitre - a.pricePerLitre)[0];
  if (expensive && expensive.pricePerLitre > avgPrice * 1.08) {
    insights.push({
      id: "price-spike",
      tone: "warning",
      title: `${prefix}Highest price paid: $${expensive.pricePerLitre.toFixed(2)}/L`,
      detail: `${expensive.fuelGrade} on ${new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(new Date(expensive.loggedAt))} — ${Math.round(((expensive.pricePerLitre - avgPrice) / avgPrice) * 100)}% above period average.`,
    });
  }

  const gradeCounts = new Map<string, number>();
  for (const log of logs) {
    gradeCounts.set(log.fuelGrade, (gradeCounts.get(log.fuelGrade) ?? 0) + 1);
  }
  const topGrade = modeKey(gradeCounts);
  if (topGrade) {
    const share = Math.round(((gradeCounts.get(topGrade) ?? 0) / logs.length) * 100);
    insights.push({
      id: "top-grade",
      tone: "neutral",
      title: `${prefix}${topGrade} dominates fill-ups`,
      detail: `${share}% of logs (${gradeCounts.get(topGrade)} of ${logs.length}) used ${topGrade}.`,
    });
  }

  const carCounts = new Map<string, number>();
  for (const log of logs) {
    carCounts.set(log.carID, (carCounts.get(log.carID) ?? 0) + log.totalCost);
  }
  if (carCounts.size > 1) {
    const sorted = [...carCounts.entries()].sort((a, b) => b[1] - a[1]);
    const topShare = Math.round((sorted[0][1] / spent) * 100);
    insights.push({
      id: "top-car",
      tone: "neutral",
      title: `${prefix}Top vehicle accounts for ${topShare}% of spend`,
      detail: "Review per-car charts to compare consumption across the fleet.",
    });
  }

  return insights;
}

export function filterFuelUserSummaries(
  summaries: FuelUserSummary[],
  filters: Pick<FuelActivityFilters, "search">
): FuelUserSummary[] {
  const query = filters.search.trim().toLowerCase();
  if (!query) return summaries;

  return summaries.filter((summary) => {
    const user = summary.user;
    const haystack = [
      user?.firstName,
      user?.lastName,
      user?.email,
      summary.userID,
      user?.country,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

export function filterEnrichedLogs(
  logs: FuelLogEnriched[],
  filters: Pick<FuelActivityFilters, "fuelGrade" | "carID" | "gasStationID" | "search">
): FuelLogEnriched[] {
  const query = filters.search.trim().toLowerCase();

  return logs.filter((log) => {
    if (filters.fuelGrade && log.fuelGrade !== filters.fuelGrade) return false;
    if (filters.carID && log.carID !== filters.carID) return false;
    if (filters.gasStationID && log.gasStationID !== filters.gasStationID) return false;
    if (!query) return true;

    const haystack = [
      log.user ? userDisplayName(log.user) : log.userID,
      log.car ? carTitle(log.car) : log.carID,
      log.station?.name,
      log.station?.address,
      log.fuelGrade,
      log.notes,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

export function previousPeriodLogs(allLogs: FuelLog[], range: FuelDateRange): FuelLog[] {
  const start = dateRangeStart(range);
  if (!start) return [];

  const current = filterLogsByDateRange(allLogs, range);
  if (current.length === 0) return [];

  const earliestCurrent = Math.min(...current.map((log) => new Date(log.loggedAt).getTime()));
  const windowMs = Date.now() - start.getTime();
  const prevStart = earliestCurrent - windowMs;

  return allLogs.filter((log) => {
    const ms = new Date(log.loggedAt).getTime();
    return ms >= prevStart && ms < start.getTime();
  });
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

export function filterLogsByYear(logs: FuelLog[], year: number): FuelLog[] {
  return logs.filter((log) => new Date(log.loggedAt).getFullYear() === year);
}

export function getAvailableFuelYears(logs: FuelLog[]): number[] {
  const years = new Set<number>();
  for (const log of logs) {
    years.add(new Date(log.loggedAt).getFullYear());
  }
  return [...years].sort((a, b) => b - a);
}

export function getFuelYearSummaries(logs: FuelLog[]): FuelYearSummary[] {
  const currentYear = new Date().getFullYear();
  return getAvailableFuelYears(logs).map((year) => ({
    year,
    overview: getFuelPlatformOverview(filterLogsByYear(logs, year)),
    isCurrentYear: year === currentYear,
  }));
}

function priorYearToDateLogs(allLogs: FuelLog[], year: number): FuelLog[] {
  const now = new Date();
  const startLastYear = new Date(year - 1, 0, 1);
  startLastYear.setHours(0, 0, 0, 0);
  const endLastYear = new Date(
    year - 1,
    year === now.getFullYear() ? now.getMonth() : 11,
    year === now.getFullYear() ? now.getDate() : 31,
    23,
    59,
    59,
    999
  );
  const startMs = startLastYear.getTime();
  const endMs = endLastYear.getTime();

  return allLogs.filter((log) => {
    const ms = new Date(log.loggedAt).getTime();
    return ms >= startMs && ms <= endMs;
  });
}

export function getYearlySpendBreakdown(
  allLogs: FuelLog[],
  users: User[],
  cars: Car[],
  stations: GasStation[],
  year: number = new Date().getFullYear()
): YearlySpendBreakdown {
  const currentYear = new Date().getFullYear();
  const isCurrentYear = year === currentYear;
  const yearLogs = filterLogsByYear(allLogs, year);
  const overview = getFuelPlatformOverview(yearLogs);

  const priorLogs = isCurrentYear
    ? priorYearToDateLogs(allLogs, year)
    : filterLogsByYear(allLogs, year - 1);
  const priorSpent = priorLogs.reduce((sum, log) => sum + log.totalCost, 0);
  const vsPriorYearPercent =
    priorSpent > 0 ? Math.round(((overview.totalSpent - priorSpent) / priorSpent) * 100) : null;

  const monthBuckets = new Map<number, { spent: number; fillUps: number; litres: number }>();
  const gradeSpend = new Map<string, number>();
  const userSpend = new Map<string, number>();
  const carSpend = new Map<string, number>();
  const stationSpend = new Map<string, number>();

  const carById = new Map(cars.map((car) => [car.carID, car]));
  const stationByUuid = new Map(stations.filter((s) => s.stationID).map((s) => [s.stationID as string, s]));
  const userById = new Map(users.filter((u) => u.userID).map((u) => [u.userID as string, u]));

  for (const log of yearLogs) {
    const monthIndex = new Date(log.loggedAt).getMonth();
    const bucket = monthBuckets.get(monthIndex) ?? { spent: 0, fillUps: 0, litres: 0 };
    bucket.spent += log.totalCost;
    bucket.fillUps += 1;
    bucket.litres += log.litres;
    monthBuckets.set(monthIndex, bucket);

    gradeSpend.set(log.fuelGrade, (gradeSpend.get(log.fuelGrade) ?? 0) + log.totalCost);
    userSpend.set(log.userID, (userSpend.get(log.userID) ?? 0) + log.totalCost);
    carSpend.set(log.carID, (carSpend.get(log.carID) ?? 0) + log.totalCost);
    stationSpend.set(log.gasStationID, (stationSpend.get(log.gasStationID) ?? 0) + log.totalCost);
  }

  const lastMonth = isCurrentYear ? new Date().getMonth() : 11;
  const monthRows: YearlySpendMonthRow[] = [];
  const spendByMonth: FuelChartDatum[] = [];

  for (let index = 0; index <= lastMonth; index += 1) {
    const bucket = monthBuckets.get(index) ?? { spent: 0, fillUps: 0, litres: 0 };
    monthRows.push({
      month: MONTH_LABELS[index],
      monthIndex: index,
      spent: Math.round(bucket.spent),
      fillUps: bucket.fillUps,
      litres: Math.round(bucket.litres * 10) / 10,
    });
    spendByMonth.push({
      name: MONTH_LABELS[index],
      value: Math.round(bucket.spent),
    });
  }

  const userSummaries = [...userSpend.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([userID, value]) => ({
      name: userById.get(userID) ? userDisplayName(userById.get(userID)!) : userID.slice(0, 8),
      value: Math.round(value),
    }));

  return {
    year,
    isCurrentYear,
    overview,
    vsPriorYearPercent,
    priorYearSpent: priorSpent > 0 ? priorSpent : null,
    spendByMonth,
    spendByGrade: [...gradeSpend.entries()].map(([name, value]) => ({
      name,
      value: Math.round(value),
      fill: fuelGradeColor(name),
    })),
    spendByUser: userSummaries,
    spendByCar: [...carSpend.entries()].map(([carID, value]) => ({
      name: carById.get(carID) ? carTitle(carById.get(carID)!) : carID.slice(0, 8),
      value: Math.round(value),
    })),
    spendByStation: [...stationSpend.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([stationId, value]) => ({
        name: stationByUuid.get(stationId)?.name ?? stationId.slice(0, 8),
        value: Math.round(value),
      })),
    monthRows,
  };
}

export function buildYearlySpendInsight(
  breakdown: YearlySpendBreakdown,
  options?: { userLabel?: string; userID?: string }
): FuelInsight | null {
  if (breakdown.overview.totalFillUps === 0) return null;

  const prefix = options?.userLabel ? `${options.userLabel}: ` : "";
  const spent = breakdown.overview.totalSpent.toLocaleString();
  const parts = [
    `${breakdown.overview.totalFillUps} fill-ups`,
    `${Math.round(breakdown.overview.totalLitres).toLocaleString()} L`,
  ];

  if (breakdown.vsPriorYearPercent != null) {
    const direction = breakdown.vsPriorYearPercent >= 0 ? "Up" : "Down";
    const comparison = breakdown.isCurrentYear ? "same period last year" : `${breakdown.year - 1}`;
    parts.unshift(`${direction} ${Math.abs(breakdown.vsPriorYearPercent)}% vs ${comparison}`);
  }

  parts.push("View yearly spend breakdown");

  return {
    id: "yearly-spend",
    tone:
      breakdown.vsPriorYearPercent != null && breakdown.vsPriorYearPercent > 10
        ? "warning"
        : breakdown.vsPriorYearPercent != null && breakdown.vsPriorYearPercent < -5
          ? "positive"
          : "neutral",
    title: `${prefix}${breakdown.year} spend: $${spent} JMD`,
    detail: parts.join(" · "),
    href: options?.userID ? `/fuel-activity/users/${options.userID}/spend` : "/fuel-activity/spend",
  };
}

export type FuelMonthSummary = {
  year: number;
  month: number;
  label: string;
  overview: FuelPlatformOverview;
};

export type MonthlySpendDayRow = {
  day: number;
  label: string;
  spent: number;
  fillUps: number;
  litres: number;
};

export type MonthlySpendBreakdown = {
  year: number;
  month: number;
  label: string;
  overview: FuelPlatformOverview;
  vsPriorMonthPercent: number | null;
  priorMonthSpent: number | null;
  spendByDay: FuelChartDatum[];
  spendByGrade: FuelChartDatum[];
  spendByUser: FuelChartDatum[];
  spendByCar: FuelChartDatum[];
  spendByStation: FuelChartDatum[];
  dayRows: MonthlySpendDayRow[];
};

export function fuelMonthLabel(year: number, month: number): string {
  return new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(
    new Date(year, month, 1)
  );
}

export function fuelMonthPath(basePath: string, year: number, month: number): string {
  return `${basePath}/${year}/${month + 1}`;
}

export function filterLogsByMonth(logs: FuelLog[], year: number, month: number): FuelLog[] {
  return logs.filter((log) => {
    const date = new Date(log.loggedAt);
    return date.getFullYear() === year && date.getMonth() === month;
  });
}

export function getCurrentMonthFuelOverview(logs: FuelLog[]): {
  year: number;
  month: number;
  label: string;
  overview: FuelPlatformOverview;
} {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const monthLogs = filterLogsByMonth(logs, year, month);

  return {
    year,
    month,
    label: fuelMonthLabel(year, month),
    overview: getFuelPlatformOverview(monthLogs),
  };
}

export function getFuelMonthSummaries(logs: FuelLog[]): FuelMonthSummary[] {
  const buckets = new Map<string, FuelLog[]>();

  for (const log of logs) {
    const date = new Date(log.loggedAt);
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const list = buckets.get(key) ?? [];
    list.push(log);
    buckets.set(key, list);
  }

  return [...buckets.entries()]
    .map(([key, monthLogs]) => {
      const [year, month] = key.split("-").map(Number);
      return {
        year,
        month,
        label: fuelMonthLabel(year, month),
        overview: getFuelPlatformOverview(monthLogs),
      };
    })
    .sort((a, b) => b.year - a.year || b.month - a.month);
}

export function getAvailableFuelMonths(logs: FuelLog[], year: number): number[] {
  const months = new Set<number>();
  for (const log of logs) {
    const date = new Date(log.loggedAt);
    if (date.getFullYear() === year) {
      months.add(date.getMonth());
    }
  }
  return [...months].sort((a, b) => a - b);
}

export function getMonthlySpendBreakdown(
  allLogs: FuelLog[],
  users: User[],
  cars: Car[],
  stations: GasStation[],
  year: number,
  month: number
): MonthlySpendBreakdown {
  const monthLogs = filterLogsByMonth(allLogs, year, month);
  const overview = getFuelPlatformOverview(monthLogs);

  const priorMonthDate = new Date(year, month - 1, 1);
  const priorMonthLogs = filterLogsByMonth(
    allLogs,
    priorMonthDate.getFullYear(),
    priorMonthDate.getMonth()
  );
  const priorSpent = priorMonthLogs.reduce((sum, log) => sum + log.totalCost, 0);
  const vsPriorMonthPercent =
    priorSpent > 0 ? Math.round(((overview.totalSpent - priorSpent) / priorSpent) * 100) : null;

  const dayBuckets = new Map<number, { spent: number; fillUps: number; litres: number }>();
  const gradeSpend = new Map<string, number>();
  const userSpend = new Map<string, number>();
  const carSpend = new Map<string, number>();
  const stationSpend = new Map<string, number>();

  const carById = new Map(cars.map((car) => [car.carID, car]));
  const stationByUuid = new Map(stations.filter((s) => s.stationID).map((s) => [s.stationID as string, s]));
  const userById = new Map(users.filter((u) => u.userID).map((u) => [u.userID as string, u]));

  for (const log of monthLogs) {
    const day = new Date(log.loggedAt).getDate();
    const bucket = dayBuckets.get(day) ?? { spent: 0, fillUps: 0, litres: 0 };
    bucket.spent += log.totalCost;
    bucket.fillUps += 1;
    bucket.litres += log.litres;
    dayBuckets.set(day, bucket);

    gradeSpend.set(log.fuelGrade, (gradeSpend.get(log.fuelGrade) ?? 0) + log.totalCost);
    userSpend.set(log.userID, (userSpend.get(log.userID) ?? 0) + log.totalCost);
    carSpend.set(log.carID, (carSpend.get(log.carID) ?? 0) + log.totalCost);
    stationSpend.set(log.gasStationID, (stationSpend.get(log.gasStationID) ?? 0) + log.totalCost);
  }

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const isCurrentMonth = year === new Date().getFullYear() && month === new Date().getMonth();
  const lastDay = isCurrentMonth ? new Date().getDate() : daysInMonth;

  const dayRows: MonthlySpendDayRow[] = [];
  const spendByDay: FuelChartDatum[] = [];

  for (let day = 1; day <= lastDay; day += 1) {
    const bucket = dayBuckets.get(day) ?? { spent: 0, fillUps: 0, litres: 0 };
    dayRows.push({
      day,
      label: String(day),
      spent: Math.round(bucket.spent),
      fillUps: bucket.fillUps,
      litres: Math.round(bucket.litres * 10) / 10,
    });
    spendByDay.push({ name: String(day), value: Math.round(bucket.spent) });
  }

  const userSummaries = [...userSpend.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([userID, value]) => ({
      name: userById.get(userID) ? userDisplayName(userById.get(userID)!) : userID.slice(0, 8),
      value: Math.round(value),
    }));

  return {
    year,
    month,
    label: fuelMonthLabel(year, month),
    overview,
    vsPriorMonthPercent,
    priorMonthSpent: priorSpent > 0 ? priorSpent : null,
    spendByDay,
    spendByGrade: [...gradeSpend.entries()].map(([name, value]) => ({
      name,
      value: Math.round(value),
      fill: fuelGradeColor(name),
    })),
    spendByUser: userSummaries,
    spendByCar: [...carSpend.entries()].map(([carID, value]) => ({
      name: carById.get(carID) ? carTitle(carById.get(carID)!) : carID.slice(0, 8),
      value: Math.round(value),
    })),
    spendByStation: [...stationSpend.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([stationId, value]) => ({
        name: stationByUuid.get(stationId)?.name ?? stationId.slice(0, 8),
        value: Math.round(value),
      })),
    dayRows,
  };
}
